/**
 * RoutePlannerToolbar - Enterprise route planning controls
 *
 * Features:
 * - Optimize route (Google Directions / Route Optimization API)
 * - Recalculate times after manual reorder
 * - Configurable default stoppage time
 * - Departure time setting
 * - Lock/unlock route
 * - Refresh data
 */

import { useState } from 'react';
import { useRoutePlannerStore } from '../../stores/route-planner-store';
import { Button, Badge } from '../ui';
import { RescheduleRouteModal } from './RescheduleRouteModal';

// ============================================================================
// ICONS
// ============================================================================

const OptimizeIcon = () => (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
  </svg>
);

const RefreshIcon = () => (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
  </svg>
);

const RecalculateIcon = () => (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
  </svg>
);

const LockIcon = () => (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
  </svg>
);

const UnlockIcon = () => (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 11V7a4 4 0 118 0m-4 8v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2z" />
  </svg>
);

const CalendarMoveIcon = () => (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
  </svg>
);

const ClockIcon = () => (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const MapPinIcon = () => (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
  </svg>
);

const SettingsIcon = () => (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
  </svg>
);

// ============================================================================
// COMPONENT
// ============================================================================

export function RoutePlannerToolbar() {
  const [showSettings, setShowSettings] = useState(false);
  const [showRescheduleModal, setShowRescheduleModal] = useState(false);

  const {
    route,
    stops,
    depot,
    isLocked,
    isOptimizing,
    isSaving,
    isGeocodingMissing,
    hasUnsavedChanges,
    defaultStoppageTime,
    departureTime,
    optimizeRoute,
    recalculateTimes,
    lockRoute,
    unlockRoute,
    refreshRoute,
    geocodeMissingStops,
    setDefaultStoppageTime,
    setDepartureTime,
    setDepot,
    resetDepotToDefault,
  } = useRoutePlannerStore();

  const missingGeocodeCount = stops.filter(
    (s) => s.latitude === null || s.longitude === null
  ).length;

  const handleStoppageTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(e.target.value, 10);
    if (!isNaN(value) && value >= 0 && value <= 60) {
      setDefaultStoppageTime(value);
    }
  };

  const handleDepartureTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setDepartureTime(e.target.value);
  };

  return (
    <div className="bg-white border-b border-gray-200 px-4 py-2">
      <div className="flex items-center justify-between gap-4">
        {/* Left: Settings panel toggle & quick settings */}
        <div className="flex items-center gap-3">
          {/* Settings toggle */}
          <button
            onClick={() => setShowSettings(!showSettings)}
            className={`p-2 rounded-md transition-colors ${
              showSettings
                ? 'bg-gray-100 text-gray-900'
                : 'text-gray-500 hover:bg-gray-50 hover:text-gray-700'
            }`}
            title="Instellingen"
          >
            <SettingsIcon />
          </button>

          {/* Quick settings inline when panel is closed */}
          {!showSettings && !isLocked && (
            <div className="flex items-center gap-4 text-sm">
              {/* Departure time */}
              <div className="flex items-center gap-2">
                <ClockIcon />
                <span className="text-gray-500">Vertrek:</span>
                <input
                  type="time"
                  value={departureTime}
                  onChange={handleDepartureTimeChange}
                  className="px-2 py-1 text-sm border border-gray-200 rounded focus:outline-none focus:ring-2 focus:ring-cinco-primary/20 focus:border-cinco-primary"
                />
              </div>

              {/* Default stoppage time */}
              <div className="flex items-center gap-2">
                <span className="text-gray-500">Stoptijd:</span>
                <input
                  type="number"
                  min={0}
                  max={60}
                  value={defaultStoppageTime}
                  onChange={handleStoppageTimeChange}
                  className="w-14 px-2 py-1 text-sm border border-gray-200 rounded focus:outline-none focus:ring-2 focus:ring-cinco-primary/20 focus:border-cinco-primary"
                />
                <span className="text-gray-500">min</span>
              </div>
            </div>
          )}
        </div>

        {/* Center: Status indicators */}
        <div className="flex items-center gap-2">
          {hasUnsavedChanges && (
            <Badge variant="warning" size="sm">
              Wijzigingen niet opgeslagen
            </Badge>
          )}
        </div>

        {/* Right: Actions */}
        <div className="flex items-center gap-2">
          {/* Geocode missing (only when there are stops without coords) */}
          {missingGeocodeCount > 0 && (
            <Button
              variant="secondary"
              size="sm"
              leftIcon={<MapPinIcon />}
              onClick={geocodeMissingStops}
              isLoading={isGeocodingMissing}
              className="border-amber-300 bg-amber-50 text-amber-900 hover:bg-amber-100"
              title="Geocode alle stops zonder coördinaten in één keer"
            >
              {isGeocodingMissing
                ? 'Bezig...'
                : `Geocode ontbrekende (${missingGeocodeCount})`}
            </Button>
          )}

          {/* Refresh */}
          <Button
            variant="ghost"
            size="sm"
            leftIcon={<RefreshIcon />}
            onClick={refreshRoute}
            disabled={isOptimizing || isSaving}
            title="Ververs data"
          >
            Ververs
          </Button>

          {/* Recalculate - always visible */}
          <Button
            variant="secondary"
            size="sm"
            leftIcon={<RecalculateIcon />}
            onClick={recalculateTimes}
            isLoading={isSaving}
            disabled={stops.length === 0}
            title="Herbereken tijden en afstanden"
          >
            Herbereken
          </Button>

          {/* Verplaats naar andere dag */}
          <Button
            variant="secondary"
            size="sm"
            leftIcon={<CalendarMoveIcon />}
            onClick={() => setShowRescheduleModal(true)}
            disabled={!route || isLocked || isOptimizing || isSaving}
            title={
              isLocked
                ? 'Ontgrendel de route eerst'
                : 'Verplaats de complete route (met alle stops) naar een andere dag'
            }
          >
            Verplaats naar andere dag
          </Button>

          {!isLocked && (
            <>
              {/* Optimize */}
              <Button
                variant="primary"
                size="sm"
                leftIcon={<OptimizeIcon />}
                onClick={optimizeRoute}
                isLoading={isOptimizing}
                disabled={stops.length === 0}
                title="Optimaliseer volgorde via Google API"
              >
                Optimaliseren
              </Button>

              {/* Lock */}
              <Button
                variant="secondary"
                size="sm"
                leftIcon={<LockIcon />}
                onClick={lockRoute}
                isLoading={isSaving}
                title="Vergrendel route (definitief maken)"
              >
                Vergrendelen
              </Button>
            </>
          )}

          {isLocked && (
            <Button
              variant="secondary"
              size="sm"
              leftIcon={<UnlockIcon />}
              onClick={unlockRoute}
              isLoading={isSaving}
              title="Ontgrendel route om wijzigingen toe te staan"
            >
              Ontgrendelen
            </Button>
          )}
        </div>
      </div>

      {/* Expanded settings panel */}
      {showSettings && !isLocked && (
        <div className="mt-3 pt-3 border-t border-gray-100 grid grid-cols-4 gap-4">
          {/* Depot / Start-eindpunt */}
          <div className="col-span-2">
            <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
              Start- en eindpunt (Depot)
            </label>
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={depot?.address || 'Oeveraseweg 13, 7971 PA Havelte'}
                onChange={(e) => {
                  if (depot) {
                    setDepot({ ...depot, address: e.target.value });
                  }
                }}
                className="flex-1 px-3 py-2 border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-cinco-primary/20 focus:border-cinco-primary"
                placeholder="Adres depot"
              />
              <button
                onClick={resetDepotToDefault}
                className="px-3 py-2 text-sm text-gray-600 hover:text-gray-800 border border-gray-200 rounded-md hover:bg-gray-50"
                title="Reset naar standaard depot (Havelte)"
              >
                Reset
              </button>
            </div>
            <p className="text-xs text-gray-400 mt-1">
              Route start en eindigt hier
            </p>
          </div>

          {/* Departure time */}
          <div>
            <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
              Vertrektijd depot
            </label>
            <input
              type="time"
              value={departureTime}
              onChange={handleDepartureTimeChange}
              className="w-full px-3 py-2 border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-cinco-primary/20 focus:border-cinco-primary"
            />
          </div>

          {/* Default stoppage time */}
          <div>
            <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
              Standaard stoptijd
            </label>
            <div className="flex items-center gap-2">
              <input
                type="number"
                min={0}
                max={60}
                value={defaultStoppageTime}
                onChange={handleStoppageTimeChange}
                className="flex-1 px-3 py-2 border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-cinco-primary/20 focus:border-cinco-primary"
              />
              <span className="text-gray-500 text-sm">min</span>
            </div>
            <p className="text-xs text-gray-400 mt-1">
              Per stop aanpasbaar in kaart
            </p>
          </div>
        </div>
      )}

      {/* Route naar andere dag verplaatsen */}
      {showRescheduleModal && route && (
        <RescheduleRouteModal
          routeId={route.id}
          routeName={route.routeName || route.name}
          oldDate={route.datum}
          chauffeurName={route.chauffeurName}
          ophalenCount={route.stopCountOphalen}
          terugbrengenCount={route.stopCountAfleveren}
          locked={isLocked}
          onClose={() => setShowRescheduleModal(false)}
        />
      )}
    </div>
  );
}
